import React, { useState, useCallback } from 'react';
import './Signup.css';
import logo from './ap-logo-4.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { auth, functions } from './firebase';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordShown, setPasswordShown] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleEmailChange = (e) => setEmail(e.target.value);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setPasswordError(e.target.value === confirmPassword ? '' : 'Passwords do not match.');
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setPasswordError(e.target.value === password ? '' : 'Passwords do not match.');
  };

  const togglePasswordVisibility = () => setPasswordShown(!passwordShown);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setPasswordError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    if (!executeRecaptcha) {
      setError('CAPTCHA not ready. Please wait a moment and try again.');
      return;
    }

    setSubmitting(true);
    try {
      const token = await executeRecaptcha('signup');
      await httpsCallable(functions, 'verifyCaptcha')({ token });
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      setSuccess('Account created successfully. You can now log in.');
      console.log('User created:', userCredential.user.uid);
    } catch (err) {
      console.error('Sign up error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [email, password, confirmPassword, executeRecaptcha]);

  return (
    <div className="auth-page signup-page">
      <div className="auth-card">
        <div className="auth-left">
          <p className="eyebrow">Join the portal</p>
          <h1>Create your client account</h1>
          <p className="lead">Access ordering, delivery windows, and invoicing in one place.</p>
          <div className="pill-group">
            <span className="pill">Order tracking</span>
            <span className="pill">Venue updates</span>
            <span className="pill">Secure access</span>
          </div>
          <div className="already-signed-up">Already have an account? <Link to="/login">Log in</Link></div>
        </div>

        <div className="auth-right">
          <div className="logo-row">
            <img src={logo} alt="Akshaya Patra Logo" className="logo" />
            <span className="brand">Akshaya Patra UK</span>
          </div>
          <h2 className="login-header">Sign up</h2>
          <form className="login-form" onSubmit={handleSubmit}>
            <label>Email</label>
            <input type="email" value={email} onChange={handleEmailChange} placeholder="you@example.com" required />
            <label>Password</label>
            <div className="password-container">
              <input type={passwordShown ? "text" : "password"} value={password} onChange={handlePasswordChange} placeholder="Create a password" required />
              <FontAwesomeIcon icon={passwordShown ? faEyeSlash : faEye} onClick={togglePasswordVisibility} className="password-icon"/>
            </div>
            {passwordError && <p className="password-error">{passwordError}</p>}
            <label>Confirm password</label>
            <input type="password" value={confirmPassword} onChange={handleConfirmPasswordChange} placeholder="Confirm password" required />
            <button type="submit" className="signin-button" disabled={submitting}>
              {submitting ? 'Creating account…' : 'Create account'}
            </button>
          </form>
          {error && <p className="signup-error">{error}</p>}
          {success && <p className="signup-success">{success}</p>}
        </div>
      </div>
    </div>
  );
};

export default Signup;
